import {
    CellRendererSelectorResult,
    ColDef,
    HeaderValueGetterParams,
    ICellRendererParams,
    IRowNode,
    PostSortRowsParams,
    ValueGetterParams,
} from '@ag-grid-community/core';
import { DatePipe } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { ColorMetadataService } from '@ddv/charts';
import {
    CrosstalkCommentCounterComponent,
    CrosstalkCustomCheckboxComponent,
    CrosstalkCustomHeaderCheckboxComponent,
    CrosstalkModalService,
    CrosstalkService,
} from '@ddv/crosstalk';
import {
    DataGridComponent,
    CustomColDef,
    GridConfiguration,
    DataGridOptions,
    HeaderCellRendererComponent,
    ColumnPicklist,
    UDFCellRendererParams,
    UDFDateCellRendererParams,
    UDFChoiceCellRendererParams,
} from '@ddv/data-grid';
import { ErrorLoggerService } from '@ddv/error-handling';
import { FormatterService } from '@ddv/formatters';
import { ApiServices, ApiExecutorService } from '@ddv/http';
import { ManagerService } from '@ddv/layout';
import {
    crosstalkCheckboxFieldId,
    CrosstalkFields,
    DdvDate,
    GRID_ICONS,
    TFL_DETAILS_AUTO_GROUP_COLUMN_ID,
    TRANSACTION_TYPE_FIELD,
    VisualizationConfigs,
    getCurrentVisualizationId,
    trebekRealTimeCommentFields,
    TrebekConversationFields,
    UserDefinedField,
    isTradeFileDetails,
    isTFLIncompleteFiles,
    QueryTypeName,
    LegacyAction,
    Action,
    isCellLevelEditActionHandler,
    WidgetData,
    GridConfigItem,
    MetadataLookup,
    moTradeReleaseCommentFieldId,
} from '@ddv/models';
import { FuzzyDate } from '@hs/ui-core-date';
import { Observable } from 'rxjs';

import { ColumnRankingService } from './column-ranking.service';
import { ColumnTransformService } from './column-transform.service';
import { GridConfigService, isManageWidgetMode } from './grid-config.service';
import { GridUriBuilderService } from './grid-uri-builder.service';
import { UserGridColumnOverridesService } from './user-grid-column-overrides.service';
import { SelectedWidgetRelayService } from '../../base/selected-widget-relay.service';
import { GroupTooltipComponent } from '../components/group-tooltip/group-tooltip.component';
import { breakEventStatus } from '../models/middle-office';

const COMMENT_COUNTER_HEADER = '<span class="hs-icon-comment"></span>';
const ALLOWED_QUERY_TYPES = [QueryTypeName.SETTLEMENT_BREAKS, QueryTypeName.TRADE_FILE_DETAILS];

@Injectable()
export class AdvancedGridConfigService extends GridConfigService {
    private userDefinedFields: Map<string, UserDefinedField[]> = new Map();
    private readonly datePipe: DatePipe;
    private readonly checkboxColumnDefinition: ColDef = {
        colId: crosstalkCheckboxFieldId,
        field: crosstalkCheckboxFieldId,
        headerComponent: CrosstalkCustomHeaderCheckboxComponent,
        cellRenderer: CrosstalkCustomCheckboxComponent,
        headerName: '',
        pinned: 'left',
        headerCheckboxSelectionFilteredOnly: true,
        resizable: false,
        width: 34,
        minWidth: 34,
        maxWidth: 34,
        pivot: false,
        enablePivot: false,
        hide: false,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        filter: false,
    };

    constructor(
        formatter: FormatterService,
        errorLoggerService: ErrorLoggerService,
        columnRankingService: ColumnRankingService,
        columnTransformService: ColumnTransformService,
        manager: ManagerService,
        crosstalkModalService: CrosstalkModalService,
        currentStateService: CurrentStateService,
        @Inject(ApiServices.trebek) private readonly trebekApiService: ApiExecutorService,
        private readonly crosstalkService: CrosstalkService,
        userGridColumnOverridesService: UserGridColumnOverridesService,
        selectedWidgetRelayService: SelectedWidgetRelayService,
        gridBuilderService: GridUriBuilderService,
        colorMetadataService: ColorMetadataService,
    ) {
        super(formatter,
            errorLoggerService,
            columnRankingService,
            columnTransformService,
            manager,
            crosstalkModalService,
            currentStateService,
            userGridColumnOverridesService,
            selectedWidgetRelayService,
            gridBuilderService,
            colorMetadataService,
        );

        this.visualizationType = 'ADVANCED_GRID';

        this.datePipe = new DatePipe('en-US');

        this.crosstalkService.userDefinedFields.subscribe((udfs) => this.userDefinedFields = udfs);

        this.userGridColumnOverridesService.setCheckboxColumnDefinition(this.checkboxColumnDefinition);
    }

    override getGridOptions(vizConfigs: VisualizationConfigs, widgetId: number): DataGridOptions {
        const gridOptions = super.getGridOptions(vizConfigs);
        gridOptions.icons = {
            ...gridOptions.icons,
            menu: `<i class="${GRID_ICONS.menu} ag-icon-menu hamburger-menu"/>`,
            columns: `<i class="${GRID_ICONS.columns} ag-icon-columns"/>`,
            columnGroupOpened: '<span class="icon-arrow-left"></span>',
            columnGroupClosed: '<span class="icon-arrow-right"></span>',
        };
        gridOptions.groupUseEntireRow = false;
        gridOptions.suppressAggFuncInHeader = false;
        gridOptions.suppressDragLeaveHidesColumns = true;
        gridOptions.suppressRowGroupHidesColumns = true;
        gridOptions.suppressGroupRowsSticky = true;
        gridOptions.groupDisplayType = 'singleColumn';
        gridOptions.rowGroupPanelShow = 'always';
        gridOptions.rowGroupPanelSuppressSort = true;
        gridOptions.pivotMode = vizConfigs.isPivotMode;
        gridOptions.autoGroupColumnDef = {
            cellClass: vizConfigs.alternateRowShading ? '' : 'no-grid-alternateshade',
            valueGetter: (params: ValueGetterParams): string => this.getAutoGroupValue(params, vizConfigs),
            headerValueGetter: (params: HeaderValueGetterParams): string => {
                const groups = params.api.getRowGroupColumns();
                return groups.length === 1 ? groups[0].getColDef().headerName! : 'Group';
            },
            cellRendererParams: {
                suppressCount: !vizConfigs.showGrouperCount,
                innerRenderer: (params: ICellRendererParams): string => this.getAutoGroupInnerRendererValue(params),
            },
            suppressSpanHeaderHeight: true,
        };
        const currentNamedQuery = this.getCurrentNamedQuery(widgetId);
        const conversableType = currentNamedQuery?.normalizeConversableType();
        const queryTypeName = currentNamedQuery?.normalizeQueryTypeName();
        const queryTypeIsTFLDetails = isTradeFileDetails(queryTypeName);
        gridOptions.rowSelection = {
            ...gridOptions.rowSelection!,
            isRowSelectable: (node: IRowNode): boolean => !!node.group || Object.prototype.hasOwnProperty.call(node, 'data'),
            enableClickSelection: !(queryTypeIsTFLDetails || conversableType),
        };
        gridOptions.rowClass = vizConfigs.alternateRowShading ? 'grid-alternateshade' : '';
        if (queryTypeIsTFLDetails) {
            gridOptions.postSortRows = (params: PostSortRowsParams): void => this.sortRowsForTFLDetails(params.nodes, queryTypeName ?? '');
        }
        gridOptions.functionsReadOnly = queryTypeIsTFLDetails;
        return gridOptions;
    }

    override getGridConfiguration(
        vizConfigs: VisualizationConfigs,
        widgetId: number,
        namedQueryId: string,
        metadata?: MetadataLookup,
        bulkCommentingOn = false,
        isUserInternal = false, // it's not actually optional, but because of the overloading we have no choice
        fuzzyDates: FuzzyDate[] = [], // it's not actually optional, but because of the overloading we have no choice
        actions?: (Action | LegacyAction)[],
        picklists?: ColumnPicklist[],
    ): GridConfiguration {
        const gridConfiguration = super.getGridConfiguration(vizConfigs, widgetId, namedQueryId, metadata);
        gridConfiguration.isRowGroupEnabled = isRowGroupEnabled(vizConfigs.configs?.values as GridConfigItem[] ?? [], metadata);

        const currentNamedQuery = this.getCurrentNamedQuery(widgetId);
        const conversableType = currentNamedQuery?.normalizeConversableType();
        const queryTypeName = currentNamedQuery?.normalizeQueryTypeName();

        gridConfiguration.columnDefinitions.forEach((colDef: ColDef | CustomColDef) => {
            this.setCustomColumns(
                colDef,
                bulkCommentingOn,
                conversableType,
                queryTypeName,
                picklists,
                actions,
                widgetId,
                isUserInternal,
                fuzzyDates);
        });

        const commentEditModeOn = gridConfiguration.columnDefinitions.some((colDef: ColDef | CustomColDef) => (
            trebekRealTimeCommentFields.includes(colDef.field!) ||
            this.isUserDefinedFieldColumn(colDef?.field) ||
            colDef.field === CrosstalkFields.Attachments
        ));

        if (commentEditModeOn) {
            this.setCrosstalkCheckboxColumnPosition(gridConfiguration, widgetId);
        }

        if (this.shouldAddCheckboxColumn(actions, queryTypeName, gridConfiguration, widgetId)) {
            this.customCheckboxColumnDefinition.cellRendererParams = { queryTypeName };
            gridConfiguration.columnDefinitions = [
                this.customCheckboxColumnDefinition,
                ...gridConfiguration.columnDefinitions,
            ];
        }

        return gridConfiguration;
    }

    override getRankedColumnIds(columnConfigs: GridConfigItem[]): string[] {
        return this.columnRankingService.getRankedColumnIds(columnConfigs);
    }

    override getExportFilteredData(
        dataGridComponent: DataGridComponent | undefined,
        preferences: VisualizationConfigs | undefined,
        isCSV = false,
        onlySelectedRows = false,
    ): WidgetData[] {
        const exportFilteredData = super.getExportFilteredData(dataGridComponent, preferences, isCSV, onlySelectedRows);
        for (const row of exportFilteredData) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete row[crosstalkCheckboxFieldId];
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete row[COMMENT_COUNTER_HEADER];
        }
        return exportFilteredData;
    }

    override getExportFilteredDataGroupers(dataGridComponent: DataGridComponent): string[] {
        return super.getExportFilteredDataGroupers(dataGridComponent);
    }

    override getFilteredExportVisibleColumns(dataGridComponent: DataGridComponent): string[] {
        return super.getFilteredExportVisibleColumns(dataGridComponent);
    }

    // this has absolutely no business being here:
    //   it has nothing to do with configuring anything
    //   it has nothing to do with a grid
    handleAction(
        clientCode: string,
        url: string,
        method: string,
        payload: { id: number }[] | { fileIds: unknown[] },
    ): Observable<{ success: boolean }> {
        return this.trebekApiService.invokeServiceWithBody(clientCode, url, method, payload);
    }

    // this has absolutely no business being here:
    //   it has nothing to do with configuring anything
    //   it has nothing to do with a grid
    uploadFile(clientCode: string, url: string, method: string, payload: FormData): Observable<{ success: boolean }> {
        return this.trebekApiService.invokeServiceWithBody(clientCode, url, method, payload);
    }

    private getAutoGroupValue(params: ValueGetterParams, vizConfigs: VisualizationConfigs): string {
        const groups = params.api.getRowGroupColumns();

        if (!groups.length) {
            return '';
        }

        const groupColumnId = groups[groups.length - 1].getColDef().field;

        if (this.isCrosstalkCommentTimeColumn(groupColumnId)) {
            return vizConfigs.repeatGroupData ? this.getCrosstalkCommentTimeFormattedValue(params.data[groupColumnId ?? ''], '') : '';
        }

        return (params.data && vizConfigs.repeatGroupData) ? params.data[groupColumnId ?? ''] : '';
    }

    private setCustomColumns(
        colDef: ColDef | CustomColDef,
        bulkCommentingOn: boolean,
        conversableType: string | undefined,
        queryTypeName: string | undefined,
        picklists: ColumnPicklist[] | undefined,
        actions: (Action | LegacyAction)[] | undefined,
        widgetId: number,
        isUserInternal: boolean,
        fuzzyDates: FuzzyDate[],
    ): void {
        if (colDef.field && trebekRealTimeCommentFields.includes(colDef.field)) {
            this.setCommentFieldColDef(colDef, bulkCommentingOn, isUserInternal);
        } else if (trebekRealTimeCommentFields.some((x) => `${x}Author` === colDef.field)) {
            colDef.cellRenderer = 'authorCellRendererComponent';
        } else if (trebekRealTimeCommentFields.some((x) => `${x}Created` === colDef.field)) {
            colDef.cellRenderer = 'datetimeCellRendererComponent';
        } else if (colDef.field === CrosstalkFields.Attachments) {
            setAttachmentsFieldColDef(colDef, conversableType);
        } else if (colDef.field === CrosstalkFields.CommentCounter) {
            setCommentCounterFieldColDef(colDef);
        } else if (this.isUserDefinedFieldColumn(colDef?.field)) {
            this.setUserDefinedFieldColDef(colDef, fuzzyDates, conversableType);
        } else if (isTFLDetailsAutoGroupColumn(queryTypeName, colDef.field)) {
            colDef.rowGroup = true;
            colDef.rowGroupIndex = 0;
        } else if (columnHasPicklist(colDef, picklists)) {
            const picklist = getColumnPicklist(colDef, picklists);
            if (picklist) {
                colDef.cellRendererParams = { picklist };
                colDef.cellRenderer = 'picklistCellRendererComponent';
                colDef.editable = false;
            }
        } else if (colDef.editable && columnAllowsBatchEdit(colDef, actions)) {
            colDef.cellRenderer = 'batchEditCellRendererComponent';
            colDef.cellEditor = 'batchEditCellEditorComponent';
        } else if (colDef.field === moTradeReleaseCommentFieldId) {
            setMoTradeReleaseCommentColDef(colDef, actions, this.isInViewMode);
        }

        if (columnAllowsBatchEdit(colDef, actions)) {
            colDef.cellRendererParams = { ...colDef.cellRendererParams, ...{ widgetId } };
            colDef.valueFormatter = (params) => params.value ?? '';
        }

        this.setColumnHeaderOptions(colDef, actions);
    }

    private setColumnHeaderOptions(colDef: ColDef | CustomColDef, actions: (Action | LegacyAction)[] | undefined): void {
        if (this.shouldAddEditIcon(colDef)) {
            colDef.headerComponent = HeaderCellRendererComponent;
            const isBatchEditColumn = columnAllowsBatchEdit(colDef, actions);
            colDef.headerComponentParams = {
                editIconText: isBatchEditColumn ? 'Editable: submit to apply changes' : 'Editable: Auto-save',
            };
        }
    }

    private shouldAddEditIcon(colDef: ColDef | CustomColDef): boolean {
        return !!(colDef.editable ||
            colDef.cellRenderer === 'picklistCellRendererComponent' ||
            this.isEditableUserDefinedFieldColumn(colDef?.field));
    }

    private getAutoGroupInnerRendererValue(params: ICellRendererParams): string {
        if (this.isCrosstalkCommentTimeColumn(params.node?.field)) {
            return this.getCrosstalkCommentTimeFormattedValue(params.value, 'Blanks');
        }

        if (this.isCrosstalkColumn(params.node?.field)) {
            return params.value || 'Blanks';
        }

        return typeof params.value !== 'boolean' && !params.value && params.valueFormatted === '' ? 'Blanks' : params.value;
    }

    private getCrosstalkCommentTimeFormattedValue(value: string, emptyStringValue: 'Blanks' | ''): string {
        return value ? (this.datePipe.transform(value, 'MM/dd/yyyy hh:mm:ss a') ?? '') : emptyStringValue;
    }

    private isColumnEditable(column: CustomColDef): boolean {
        return !(this.isCrosstalkCommentColumn(column.field)) &&
            column.field !== CrosstalkFields.Attachments &&
            !column.isUserDefinedField &&
            !!column.editable;
    }

    private isCrosstalkColumn(field?: string | null): boolean {
        return this.isCrosstalkCommentColumn(field) ||
            this.isCrosstalkCommentAuthorColumn(field) ||
            this.isCrosstalkCommentTimeColumn(field);
    }

    private isCrosstalkCommentColumn(field?: string | null): boolean {
        return field === TrebekConversationFields.ClientComment || field === TrebekConversationFields.HSComment;
    }

    private isCrosstalkCommentAuthorColumn(field?: string | null): boolean {
        return field === `${TrebekConversationFields.ClientComment}Author` || field === `${TrebekConversationFields.HSComment}Author`;
    }

    private isCrosstalkCommentTimeColumn(field?: string | null): boolean {
        return field === `${TrebekConversationFields.ClientComment}Created` || field === `${TrebekConversationFields.HSComment}Created`;
    }

    private setCommentFieldColDef(colDef: CustomColDef, bulkCommentingOn: boolean, isUserInternal: boolean): void {
        colDef.cellRenderer = null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        colDef.cellRendererSelector = (params): any => (
            isCrosstalkRow(params.node) ? { component: 'commentCellRendererComponent', params } : null
        );

        const userCanEditCell = (isUserInternal && colDef.field !== TrebekConversationFields.ClientComment) ||
            (!isUserInternal && colDef.field === TrebekConversationFields.ClientComment);

        if (userCanEditCell) {
            colDef.editable = bulkCommentingOn ?
                ({ node }): boolean => isCrosstalkSelectedRow(node) :
                ({ node }): boolean => isCrosstalkRow(node);

            colDef.cellEditor = 'commentCellEditorComponent';
            colDef.cellClassRules = {
                selected: ({ node }): boolean => isCrosstalkSelectedRow(node),
                'inline-background': ({ node }): boolean => isCrosstalkRow(node),
            };
        }
    }

    private setUserDefinedFieldColDef(colDef: CustomColDef, fuzzyDates: FuzzyDate[], conversableType?: string): void {
        const type = colDef.userDefinedFieldType;
        const canEditUdf = !!colDef.editable;

        colDef.isUserDefinedField = true;
        colDef.cellRenderer = null;
        colDef.cellRendererSelector = (params): CellRendererSelectorResult | undefined => isCrosstalkRow(params.node) ?
            {
                component: colDef.field?.endsWith('_commentTS') ?
                    'datetimeCellRendererComponent' :
                    'userDefinedFieldCellRendererComponent',
                params,
            } :
            undefined;

        if (type === 'date') {
            this.setDateUDF(canEditUdf, colDef, fuzzyDates);
        } else if (type === 'choice') {
            this.setChoiceUDF(canEditUdf, colDef, conversableType);
        } else if (type === 'string') {
            this.setStringUDF(canEditUdf, colDef);
        } else if (type === 'decimal') {
            this.setDecimalUDF(canEditUdf, colDef);
        } else if (type === 'boolean') {
            this.setBooleanUDF(canEditUdf, colDef);
        }
    }

    private setCrosstalkCheckboxColumnPosition(gridConfiguration: GridConfiguration, widgetId: number): void {
        const columnDefinitions = gridConfiguration.columnDefinitions;
        const checkboxColumnIndex = columnDefinitions.findIndex((col: ColDef) => col.colId === crosstalkCheckboxFieldId);
        if (checkboxColumnIndex > -1) {
            const visualizationId = getCurrentVisualizationId(this.getWidgetPreferences(widgetId)) ?? 0;
            this.userGridColumnOverridesService.setCheckboxColumnPosition(
                widgetId,
                visualizationId,
                columnDefinitions,
                'ColDef',
                this.isInViewMode,
                'ADVANCED_GRID',
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const checkboxColumn: any | undefined = columnDefinitions.find((c: ColDef) => c.colId === crosstalkCheckboxFieldId);
            this.checkboxColumnDefinition.pinned = checkboxColumn?.pinned;
            gridConfiguration.columnDefinitions.splice(checkboxColumnIndex, 1, this.checkboxColumnDefinition);
        } else {
            this.checkboxColumnDefinition.pinned = 'left';
            gridConfiguration.columnDefinitions = columnDefinitions;
            if (!gridConfiguration.gridOptions.pivotMode) {
                gridConfiguration.columnDefinitions.unshift(this.checkboxColumnDefinition);
            }
        }
    }

    // yes, I know we are modifying nodes in here
    // but it looks like this is the way for now
    // https://ag-grid.com/angular-data-grid/row-sorting/#post-sort
    private sortRowsForTFLDetails(nodes: IRowNode[], queryTypeName: string): void {
        if (nodes.every((node) => this.rowHasGroupId(node) && isTFLDetailsAutoGroupColumn(queryTypeName, node.parent?.field))) {
            const blockIndex = nodes.findIndex((node) => node.data[TRANSACTION_TYPE_FIELD]);
            if (blockIndex !== -1) {
                const block = nodes.splice(blockIndex, 1)[0];
                nodes.unshift(block);
            }
        }
    }

    private rowHasGroupId(node: IRowNode): boolean {
        return !!node.data?.[TFL_DETAILS_AUTO_GROUP_COLUMN_ID];
    }

    private shouldAddCheckboxColumn(
        actions: (Action | LegacyAction)[] | undefined,
        queryTypeName: string | undefined,
        gridConfiguration: GridConfiguration,
        widgetId: number,
    ): boolean {
        const hasRowLevelAction = actions?.some((action) => action.type === 'each');
        const hasEditableColumn = gridConfiguration.columnDefinitions.some((column) => this.isColumnEditable(column));

        const tradeReleaseCommentColumn =
            gridConfiguration.columnDefinitions.find((column: ColDef) => column.field === moTradeReleaseCommentFieldId);
        if (isManageWidgetMode(widgetId) && tradeReleaseCommentColumn) {
            return false;
        }

        return hasRowLevelAction ||
            isTFLIncompleteFiles(queryTypeName) ||
            (ALLOWED_QUERY_TYPES.includes(queryTypeName as QueryTypeName) && hasEditableColumn);
    }

    private setDateUDF(canEditUdf: boolean, colDef: CustomColDef, fuzzyDates: FuzzyDate[]): void {
        const cellRendererParams = {
            userDefinedFieldType: 'date',
            fuzzyDates,
            canEditUdf,
        } as UDFDateCellRendererParams;
        const comparator = (a: { value: string }, b: { value: string }): number => {
            const valueA = cellHasNoValue(a) ? 0 : DdvDate.fromDashFormat(a.value).toMilliseconds();
            const valueB = cellHasNoValue(b) ? 0 : DdvDate.fromDashFormat(b.value).toMilliseconds();
            return valueA - valueB;
        };

        colDef.cellRendererParams = {
            ...colDef.cellRendererParams,
            ...cellRendererParams,
        };
        colDef.comparator = comparator;
        colDef.cellClass = 'user-defined-field-cell';
    }

    private setChoiceUDF(canEditUdf: boolean, colDef: CustomColDef, conversableType: string | undefined): void {
        const cellRendererParams = {
            userDefinedFieldType: 'choice',
            choices: this.userDefinedFields.get(conversableType ?? '')?.find((udf) => `udf_${udf.name}` === colDef.field)?.choices ?? [],
            canEditUdf,
        } as UDFChoiceCellRendererParams;

        const comparator = (a: { value: string }, b: { value: string }): number => {
            const valueA: string = cellHasNoValue(a) ? '' : a.value;
            const valueB: string = cellHasNoValue(b) ? '' : b.value;
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        };

        colDef.cellRendererParams = {
            ...colDef.cellRendererParams,
            ...cellRendererParams,
        };
        colDef.comparator = comparator;
        colDef.editable = false;
        colDef.cellClass = 'user-defined-field-cell';
    }

    private setStringUDF(canEditUdf: boolean, colDef: CustomColDef): void {
        const comparator = (a: { value: string }, b: { value: string }): number => {
            const valueA: string = cellHasNoValue(a) ? '' : a.value;
            const valueB: string = cellHasNoValue(b) ? '' : b.value;
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        };
        if (canEditUdf) {
            colDef.cellEditor = 'userDefinedFieldCellEditorComponent';
            colDef.editable = (params): boolean => !params.node.isRowPinned() && !params.node.allChildrenCount;
        }
        colDef.cellRendererParams = {
            ...colDef.cellRendererParams,
            userDefinedFieldType: 'string',
        } as UDFCellRendererParams;
        colDef.comparator = comparator;
    }

    private setDecimalUDF(canEditUdf: boolean, colDef: CustomColDef): void {
        const comparator = (a: { value: number }, b: { value: number }): number => {
            const valueA: number = cellHasNoValue(a) ? 0 : a.value;
            const valueB: number = cellHasNoValue(b) ? 0 : b.value;
            return valueA - valueB;
        };

        if (canEditUdf) {
            colDef.cellEditor = 'userDefinedFieldCellEditorComponent';
            colDef.editable = (params): boolean => !params.node.isRowPinned() &&
                !((params.api.isPivotMode() || !!params.node.allChildrenCount));
        }

        colDef.valueGetter = (params): number | undefined => {
            if (params.api && (params.api.isPivotMode() || params.api.getRowGroupColumns().length)) {
                if (!params.column.getAggFunc()) {
                    return undefined;
                }

                const value = Number(params.data?.[params.colDef.field ?? '']?.value);
                return isNaN(value) ? undefined : value;
            }

            return params.data?.[params.colDef.field ?? '']?.value;
        };

        colDef.cellRendererParams = {
            ...colDef.cellRendererParams,
            userDefinedFieldType: 'decimal',
        } as UDFCellRendererParams;
        colDef.comparator = comparator;
    }

    private setBooleanUDF(canEditUdf: boolean, colDef: CustomColDef): void {
        const comparator = (a: { value: boolean }, b: { value: boolean }): number => {
            const valueA: boolean = cellHasNoValue(a) ? false : (a.value);
            const valueB: boolean = cellHasNoValue(b) ? false : (b.value);
            return Number(valueA) - Number(valueB);
        };
        colDef.cellRendererParams = {
            ...colDef.cellRendererParams,
            userDefinedFieldType: 'boolean',
            canEditUdf,
        } as UDFCellRendererParams;
        colDef.comparator = comparator;
    }
}

export function isTFLDetailsAutoGroupColumn(queryTypeName?: string, field?: string | null): boolean {
    return isTradeFileDetails(queryTypeName) && field === TFL_DETAILS_AUTO_GROUP_COLUMN_ID;
}

function setMoTradeReleaseCommentColDef(
    colDef: ColDef,
    actions: (Action | LegacyAction)[] | undefined,
    isInViewMode = false,
): void {
    colDef.cellRenderer = 'moTradeReleaseCommentRendererComponent';
    colDef.cellEditor = 'moTradeReleaseCommentEditorComponent';
    colDef.editable = (params): boolean => {
        const fieldHasValue = params.data?.[params.colDef.field ?? '']?.value;
        const statusIsCreated = params.data?.[breakEventStatus] === 'Created';
        return !!(
            isInViewMode &&
            !fieldHasValue &&
            statusIsCreated &&
            actions?.length
        );
    };
}

function setAttachmentsFieldColDef(colDef: ColDef, conversableType?: string): void {
    colDef.headerName = '';
    colDef.enableRowGroup = false;
    colDef.cellRenderer = null;
    colDef.sortable = false;
    colDef.suppressColumnsToolPanel = true;
    colDef.minWidth = 60;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    colDef.cellRendererSelector = (params): any => (
        isCrosstalkRow(params.node) ? { component: 'attachmentsCellRendererComponent', params } : null
    );
    colDef.cellRendererParams = { conversableType };
}

function setCommentCounterFieldColDef(colDef: ColDef): void {
    colDef.headerComponent = HeaderCellRendererComponent;
    colDef.cellRenderer = CrosstalkCommentCounterComponent;
    colDef.headerName = COMMENT_COUNTER_HEADER;
    colDef.enableRowGroup = false;
    colDef.resizable = true;
    colDef.minWidth = 60;
    colDef.width = 60;
    colDef.pivot = false;
    colDef.enablePivot = false;
    colDef.suppressColumnsToolPanel = true;
    colDef.filter = 'agNumberColumnFilter';
    colDef.filterValueGetter = (params: ValueGetterParams): number => params.data?.[CrosstalkFields.CommentCounter];
    colDef.comparator = (a: number, b: number): number => a - b;
    colDef.valueGetter = (params): string | ((params: ValueGetterParams) => number) =>
        params.data?.[CrosstalkFields.CommentCounter];
    colDef.tooltipComponent = GroupTooltipComponent;
}

function isCrosstalkRow(node: IRowNode): boolean {
    return !node.isRowPinned() && !node.group;
}

function isCrosstalkSelectedRow(node: IRowNode): boolean {
    return !!(node.isSelected() && isCrosstalkRow(node));
}

function isRowGroupEnabled(columnConfigs: GridConfigItem[], metadata?: MetadataLookup): boolean {
    if (!metadata) {
        return false;
    }
    return columnConfigs.some((columnConfig) => metadata[columnConfig.value ?? '']?.canPivotOn);
}

function columnAllowsBatchEdit(colDef: ColDef, actions: (Action | LegacyAction)[] | undefined): boolean {
    return !!(actions?.some((a) => {
        return isCellLevelEditActionHandler(a.handler) && a.handler.submitInBatch && a.handler.fieldName === colDef.field;
    }));
}

function getColumnPicklist(colDef: ColDef | CustomColDef, columnPicklist: ColumnPicklist[] = []): ColumnPicklist | undefined {
    return columnPicklist.find((cp) => cp.field === colDef.field);
}

function columnHasPicklist(colDef: ColDef | CustomColDef, columnPicklist: ColumnPicklist[] = []): boolean {
    return columnPicklist.some((cp) => cp.field === colDef.field);
}

function cellHasNoValue(data: { value: unknown } | null | undefined): boolean {
    return data?.value == null;
}
